import React, { useState, useRef, useEffect } from 'react'
import styled, { useTheme } from 'styled-components'

import { IconArrowDown } from 'components/Icons/IconArrowDown'
import { IconArrowUp } from 'components/Icons/IconArrowUp'
import { useIsMobile } from 'utils/useIsMobile'
import { Stylable } from 'utils/types'

interface ExpansionPanelProps extends Stylable {
  title: React.ReactNode
  children: React.ReactNode
  defaultExpanded?: boolean
}

const PanelWrapper = styled.div`
  border-radius: 12px;
  margin-bottom: 16px;
  overflow: hidden;
  border: 1px solid ${p => p.theme.colors.border};
  background: ${p => p.theme.colors.background.secondary};
`

const PanelHeader = styled.div`
  padding: 16px 20px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: space-between;
  transition: background-color 0.3s ease;

  .arrow-icon {
    display: flex;
    transition: transform 0.3s ease;
  }
`

const PanelContentWrapper = styled.div<{ expanded: boolean; height: number }>`
  height: ${props => (props.expanded ? `${props.height}px` : '0px')};
  transition: height 0.4s ease;
  overflow: hidden;
`

const PanelContentOuter = styled.div`
  padding: 0 20px;
  background: ${p => p.theme.colors.background.secondary};
`

const InnerContent = styled.div`
  border-top: 1px solid ${p => p.theme.colors.divider};
  padding: 16px 0;
  box-sizing: border-box;
`

export const ExpansionPanel: React.FC<ExpansionPanelProps> = ({ title, children, defaultExpanded = false, style }) => {
  const [expanded, setExpanded] = useState(defaultExpanded)
  const [height, setHeight] = useState(0)

  const isMobile = useIsMobile()

  const contentRef = useRef<HTMLDivElement>(null)

  const theme = useTheme()

  useEffect(() => {
    if (expanded && contentRef.current) {
      setHeight(contentRef.current.scrollHeight)
    } else {
      setHeight(0)
    }
  }, [expanded, children])

  return (
    <PanelWrapper style={style}>
      <PanelHeader onClick={() => setExpanded(!expanded)}>
        <div style={{ width: isMobile ? '93%' : '95%' }}>{title}</div>
        <div className="arrow-icon">
          {expanded ? <IconArrowUp stroke={theme.colors.text.primary} /> : <IconArrowDown />}
        </div>
      </PanelHeader>

      <PanelContentWrapper expanded={expanded} height={height}>
        <PanelContentOuter ref={contentRef}>
          <InnerContent>{children}</InnerContent>
        </PanelContentOuter>
      </PanelContentWrapper>
    </PanelWrapper>
  )
}
